import {
  type ComponentPropsWithoutRef,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './ImageGenerationPreview.module.css';

import clsxm from '@/lib/clsxm';

import GenerationElapsedTime from './GenerationElapsedTime';

const REVEAL_DURATION_MS = 1200;

/** A fine, stationary dot grid whose individual dots wake and fade like noise. */
const DotNoise = memo(function DotNoise({ active }: { active: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let visible = true;
    let width = 0;
    let height = 0;
    let lastFrame = 0;
    let elapsed = 0;
    let dots: { x: number; y: number; phase: number; radius: number }[] = [];

    const draw = (time: number) => {
      context.clearRect(0, 0, width, height);
      context.fillStyle = getComputedStyle(canvas).color;
      // Batch similarly opaque dots into eight paths instead of one draw per dot.
      const buckets: (typeof dots)[] = Array.from({ length: 8 }, () => []);
      for (const dot of dots) {
        const wave =
          Math.sin(dot.x * 0.045 + time * 0.8) *
          Math.cos(dot.y * 0.035 - time * 0.6);
        const noise = (Math.sin(dot.phase + time * 2.4 + wave * 2) + 1) / 2;
        buckets[Math.min(7, Math.floor(noise * 8))].push(dot);
      }
      buckets.forEach((bucket, index) => {
        context.globalAlpha = 0.035 + (index / 7) ** 2 * 0.3;
        context.beginPath();
        for (const dot of bucket) {
          context.moveTo(dot.x + dot.radius, dot.y);
          context.arc(dot.x, dot.y, dot.radius, 0, Math.PI * 2);
        }
        context.fill();
      });
    };

    const tick = (now: number) => {
      // 30fps is plenty for fine noise; animation never updates React state.
      if (now - lastFrame >= 1000 / 30) {
        elapsed += Math.min(now - lastFrame, 64) / 1000;
        lastFrame = now;
        draw(elapsed);
      }
      frame = requestAnimationFrame(tick);
    };

    const sync = () => {
      cancelAnimationFrame(frame);
      draw(elapsed);
      if (active && !motion.matches && visible && !document.hidden) {
        lastFrame = performance.now();
        frame = requestAnimationFrame(tick);
      }
    };

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = bounds.width;
      height = bounds.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      dots = [];
      // Keep the reference's delicate spacing, with a bound for large row views.
      const spacing = Math.max(5, Math.sqrt((width * height) / 6500));
      for (let y = 2.5; y < height; y += spacing) {
        for (let x = 2.5; x < width; x += spacing) {
          const seed = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
          const noise = seed - Math.floor(seed);
          dots.push({
            x: x + (noise - 0.5) * 0.65,
            y: y + (noise - 0.5) * 0.65,
            phase: noise * Math.PI * 2,
            radius: (0.35 + noise * 0.45) * 1.7,
          });
        }
      }
      sync();
    };

    const resizeObserver = new ResizeObserver(resize);
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      sync();
    });
    resizeObserver.observe(canvas);
    intersectionObserver.observe(canvas);
    motion.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    resize();

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      motion.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [active]);

  return <canvas ref={canvasRef} className={styles.dots} aria-hidden='true' />;
});

type ImageGenerationPreviewProps = Pick<
  ComponentPropsWithoutRef<'img'>,
  'alt' | 'title' | 'onClick' | 'onError'
> & {
  src?: string;
  className?: string;
  imageClassName?: string;
  children?: ReactNode;
  /** Enable the reveal for a new synchronous result that already has a URL. */
  animateReveal?: boolean;
  /** Original request time; retained when the optimistic placeholder is replaced. */
  generationStartedAt?: number;
  loadFailed?: boolean;
  naturalAspect?: boolean;
  statusLabel: string;
};

/** Keeps the same noise surface mounted from generation through image loading. */
function ImageGenerationPreview({
  src,
  alt,
  title,
  className,
  imageClassName,
  children,
  animateReveal = false,
  generationStartedAt,
  loadFailed = false,
  naturalAspect = false,
  statusLabel,
  onClick,
  onError,
}: ImageGenerationPreviewProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  const revealFrame = useRef(0);
  const [loadedSrc, setLoadedSrc] = useState<string>();
  const [finishedSrc, setFinishedSrc] = useState<string>();
  const [hasGenerated, setHasGenerated] = useState(animateReveal || !src);
  const ready = Boolean(src && loadedSrc === src);
  const finished = Boolean(src && finishedSrc === src);
  const animated = hasGenerated || animateReveal;

  const handleLoad = useCallback(() => {
    cancelAnimationFrame(revealFrame.current);
    if (
      !animated ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setLoadedSrc(src);
      return;
    }
    // Give even cached images a painted, blurred starting frame before revealing.
    revealFrame.current = requestAnimationFrame(() => {
      revealFrame.current = requestAnimationFrame(() => setLoadedSrc(src));
    });
  }, [src, animated]);

  useEffect(() => {
    if (!src) {
      setHasGenerated(true);
      setLoadedSrc(undefined);
      setFinishedSrc(undefined);
    } else if (
      imageRef.current?.complete &&
      imageRef.current.naturalWidth > 0
    ) {
      // Cached images may finish before React attaches the load handler.
      handleLoad();
    }
    return () => cancelAnimationFrame(revealFrame.current);
  }, [src, handleLoad]);

  useEffect(() => {
    if (!ready) return;
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    const timer = window.setTimeout(
      () => setFinishedSrc(src),
      animated && !reducedMotion ? REVEAL_DURATION_MS : 0,
    );
    return () => window.clearTimeout(timer);
  }, [src, ready, animated]);

  return (
    <div
      className={clsxm(
        styles.preview,
        'group relative flex w-full items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800',
        naturalAspect ? !ready && 'aspect-[4/3]' : 'aspect-square',
        className,
      )}
      data-state={finished ? 'resolved' : ready ? 'revealing' : 'generating'}
      data-animate={animated || undefined}
      aria-busy={!ready && !loadFailed}
    >
      {src && (
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          title={title}
          loading={animated ? 'eager' : 'lazy'}
          className={clsxm(styles.image, imageClassName)}
          onLoad={handleLoad}
          onError={onError}
          onClick={onClick}
        />
      )}
      {!finished && (
        <div className={styles.placeholder} aria-hidden='true'>
          <div className={styles.surface} />
          <DotNoise active={animated && !loadFailed} />
        </div>
      )}
      {!ready && !loadFailed && (
        <span role='status' className='sr-only'>
          {statusLabel}
        </span>
      )}
      {!finished && animated && !loadFailed && (
        <GenerationElapsedTime
          startedAt={generationStartedAt}
          running={!ready}
        />
      )}
      {children}
    </div>
  );
}

export default memo(ImageGenerationPreview);
